/**
 * Application shell and router.
 *
 * Renders the routed content column, the brand masthead, and the bottom
 * navigation, and gates the app behind sign-in. The masthead (AppHeader) carries
 * the menu button, whose drawer holds the account-wide destinations — approvals,
 * security, language, developer mode — and Sign out. The home header keeps the
 * account switcher and the tier pill, which are dashboard-scoped and belong with
 * the balances. The navigation is only rendered once a session is present, so
 * the sign-in screen never shows it. The login and OAuth callback routes render
 * unauthenticated; every other route redirects to /login without a session.
 * Session loss — sign-out, a rejected refresh, or the expiry watchdog finding the
 * refresh token dead — clears the account slice, which flips the guard back to the
 * login screen. It also dispatches SESSION_EXPIRED_EVENT, which this module listens
 * for and turns into an explicit navigation, so the redirect does not rest on the
 * guard alone.
 */

import { useEffect, useLayoutEffect, useSyncExternalStore, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

import AppErrorBoundary from '@/components/AppErrorBoundary';
import RouteScreen from '@/components/RouteScreen';
import {
  HomeSkeleton, ReceiveSkeleton, SwapSkeleton, ActivitySkeleton,
} from '@/components/PageSkeletons';
import { lazyWithReload } from '@/lib/lazyRetry';

// Statically imported, not lazy: it renders when the API is refusing, and a
// screen that explains an outage must not itself depend on fetching a chunk.
import Maintenance from './views/Maintenance';
import { isMaintenance, subscribeMaintenance } from './lib/maintenance';
import AppHeader from './components/AppHeader';
import AppNav from './components/AppNav';
import PageHeaderProvider from './components/PageHeaderProvider';
import CreatePasskeyPrompt from './components/CreatePasskeyPrompt';
import Toaster from './components/Toaster';
import { SwapTrackerGate } from './components/SwapProgress';
import { isCreatePasskeyDismissed } from './lib/createPasskeyPref';

// Route-level views are code-split so the initial bundle carries only the shell
// and the first route. Vite emits each as its own hashed chunk, fetched on first
// navigation, which keeps the initial payload small for a faster first paint.
const Dashboard = lazyWithReload(() => import('./views/Dashboard'));
const Login = lazyWithReload(() => import('./views/Login'));
const LanguageAppearance = lazyWithReload(() => import('@/views/LanguageAppearance'));
const Start = lazyWithReload(() => import('./views/Start'));
const Provisioning = lazyWithReload(() => import('./views/Provisioning'));
const OAuthCallback = lazyWithReload(() => import('./views/OAuthCallback'));
const Send = lazyWithReload(() => import('./views/Send'));
const Receive = lazyWithReload(() => import('./views/Receive'));
const Create = lazyWithReload(() => import('./views/Create'));
const Activity = lazyWithReload(() => import('./views/Activity'));
const Swap = lazyWithReload(() => import('./views/Swap'));
const Earn = lazyWithReload(() => import('./views/Earn'));
const Approvals = lazyWithReload(() => import('./views/Approvals'));
const SecuritySettings = lazyWithReload(() => import('./views/SecuritySettings'));
const DeveloperSettings = lazyWithReload(() => import('./views/DeveloperSettings'));
const Welcome = lazyWithReload(() => import('./views/Welcome'));

import { SESSION_EXPIRED_EVENT } from './lib/auth';
import { useSlice, useAuthed } from './store/hooks';
import { setDeployment, setFreshLogin } from './store/store';
import { getDeploymentConfig } from './lib/api';
import { loadDevices } from './services/devices';
import { startHomePreferences } from './services/preferences';
import { useLocale, resolveDocumentLocale } from './i18n';
import {
  isRoute,
  ROUTE_WELCOME,
  ROUTE_START,
  ROUTE_LANGUAGE_APPEARANCE,
  ROUTE_LOGIN,
  ROUTE_OAUTH_CALLBACK,
  ROUTE_SWAP,
  ROUTE_SECURITY,
  ROUTE_DEVELOPER,
  ROUTE_PROVISIONING,
  ROUTE_CREATE,
} from './lib/routes';

/** Apply the theme preference to the document root whenever it changes. */
function useThemeEffect(): void {
  const ui = useSlice('ui');
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', ui.theme);
  }, [ui.theme]);
}

/**
 * Load the deployment config once so the nav can show the tier and views can
 * classify wallets. The environment label is cosmetic; the authoritative tier
 * signals are serves_mainnet, serves_testnet, bitcoin_network, and
 * bitcoin_mainnet_served (which gates the Bitcoin create option). On error we
 * seed servesMainnet null, which fails safe to testnet so nothing is labeled
 * mainnet before the truth is known.
 */
function useEnvironment(): void {
  useEffect(() => {
    let cancelled = false;
    getDeploymentConfig()
      .then((cfg) => {
        const c = cfg && typeof cfg === 'object' ? (cfg as Record<string, unknown>) : {};
        const environment = typeof c.network_env === 'string' ? c.network_env : 'unknown';
        const servesMainnet = typeof c.serves_mainnet === 'boolean' ? c.serves_mainnet : null;
        const servesTestnet = typeof c.serves_testnet === 'boolean' ? c.serves_testnet : null;
        const bitcoinNetwork = typeof c.bitcoin_network === 'string' ? c.bitcoin_network : null;
        const bitcoinMainnetServed =
          typeof c.bitcoin_mainnet_served === 'boolean' ? c.bitcoin_mainnet_served : null;
        if (!cancelled) {
          setDeployment({
            environment,
            servesMainnet,
            servesTestnet,
            bitcoinNetwork,
            bitcoinMainnetServed,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDeployment({
            environment: 'unknown',
            servesMainnet: null,
            servesTestnet: null,
            bitcoinNetwork: null,
            bitcoinMainnetServed: null,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);
}

/**
 * Load the caller's enrolled device passkeys once a session is present so the
 * send path knows whether to run the device assertion and the Security section
 * can render the list. Re-runs when the session flips so a fresh sign-in reloads
 * for the new user. A load failure is swallowed inside the service, which records
 * the feature as unsupported or surfaces a calm error; it never blocks the app.
 */
function useDevices(authed: boolean, sessionEpoch: number): void {
  useEffect(() => {
    if (!authed) return;
    const controller = new AbortController();
    void loadDevices({ signal: controller.signal });
    return () => controller.abort();
  }, [authed, sessionEpoch]);
}

/**
 * Route to the sign-in screen the moment a session ends.
 *
 * The event this listens for has been dispatched since the auth module was
 * written, described there as "the single coupling point between this module and
 * the boot wiring" — but nothing was ever listening, so the coupling did not
 * exist and the redirect rested entirely on the account slice flipping.
 *
 * That flip does still do the work in the ordinary case, which makes this a
 * second, independent path rather than a replacement. It matters where the store
 * flip alone is not enough: the user may be sitting on a public route, or on a
 * screen whose own state would otherwise keep rendering, and a navigation is a
 * far more direct statement of "your session ended" than waiting for a guard to
 * re-evaluate. Navigating when already signed out is harmless — the login route
 * simply renders.
 */
function useSessionExpiredRedirect(): void {
  const navigate = useNavigate();
  useEffect(() => {
    const onSessionExpired = (): void => {
      // Carry the query string across, exactly as RequireAuth does. A bare
      // /login would drop an `?org=<id>` deep link, and because this handler runs
      // synchronously from the dispatch it wins the race against RequireAuth's
      // own redirect — so dropping it here would silently sign an admin or an
      // invited member into their personal org instead of the org the link named.
      // Read from window rather than useLocation so the value is current at the
      // moment the session ends, not whatever it was when the effect was wired.
      navigate({ pathname: ROUTE_LOGIN, search: window.location.search }, { replace: true });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [navigate]);
}

/** Redirect to the sign-in screen when no session is present. */
function RequireAuth({ children }: { children: ReactNode }) {
  const authed = useAuthed();
  const location = useLocation();
  // Carry the query string onto the login route so a deep link that names an org
  // to sign in to, for example `/?org=<id>` for an admin or invited member,
  // survives the redirect. The sign-in action reads the org from the URL, so
  // dropping the query here would silently fall back to self-service and land
  // the user in their personal org instead of the org the link named.
  if (!authed)
    return (
      <Navigate to={`/login${location.search}`} replace state={{ from: location.pathname }} />
    );
  return <>{children}</>;
}

/**
 * Sole owner of `<html lang>`. It has to live inside the router because the value
 * depends on the route — the welcome screen always renders English even when a
 * different locale is stored — and LanguageProvider sits outside the router, so it
 * cannot see the path. main.tsx applies the same resolver once before first render
 * (so the first paint picks the right font); this keeps it correct as the route,
 * locale, or auth state changes.
 *
 * useLayoutEffect, not useEffect: the attribute selects --font-sans, so applying it
 * before the browser paints avoids a flash of the wrong font.
 */
function useDocumentLanguage(authed: boolean): void {
  const { locale, localeChosen } = useLocale();
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    document.documentElement.lang = resolveDocumentLocale({
      locale,
      localeChosen,
      pathname,
      authed,
    });
  }, [locale, localeChosen, pathname, authed]);
}

/**
 * First-visit language gate. A genuine first-time visitor is sent to /welcome once
 * to pick a language; the choice settles (localeChosen) so it never shows again.
 *
 * It deliberately does NOT gate an authenticated user (they have clearly used the
 * app) or any returning user with prior state — localeChosen already accounts for
 * both, and skipping authed here also keeps the signed-in chrome (nav, prompts)
 * from rendering behind a redirect. The welcome route and the OAuth callback are
 * exempt so the gate can neither loop nor interrupt a sign-in return. The intended
 * destination is carried in navigation state so a deep link (e.g. /?org=<id>)
 * survives the language choice instead of collapsing to '/'.
 */
function WelcomeGate({ children }: { children: ReactNode }) {
  const authed = useAuthed();
  const { localeChosen } = useLocale();
  const { pathname, search } = useLocation();
  if (
    authed ||
    localeChosen ||
    isRoute(pathname, ROUTE_WELCOME) ||
    isRoute(pathname, ROUTE_OAUTH_CALLBACK)
  ) {
    return <>{children}</>;
  }
  return <Navigate to={ROUTE_WELCOME} replace state={{ from: pathname + search }} />;
}

/**
 * The one-time create-a-passkey prompt for a fresh OAuth or OTP login. It renders
 * only when every gate holds: the in-memory fresh-login flag is set (storeLogin
 * sets it for a non-passkey provider and clears here), the browser supports
 * WebAuthn, the enrolled-device list has loaded and is empty, and the user has not
 * chosen never to see the offer again. Requiring the list to be loaded avoids
 * offering a passkey before the app knows whether one already exists; the empty
 * check stops the offer for a user who already enrolled one. Because the flag lives
 * in memory for this session alone, an ordinary reload or a passkey login never
 * shows it, and a user who skips can still add one later from Security in the menu.
 *
 * What keeps this away from a fresh passkey signup is freshLogin, not passkeySignup:
 * storeLogin raises freshLogin only for a non-passkey provider, and a signup reports
 * 'passkey', so the first condition below is already false. The `!passkeySignup` term
 * is a redundant second guard in every reachable state.
 */
function CreatePasskeyPromptGate() {
  const accounts = useSlice('accounts');
  const devices = useSlice('devices');

  const eligible =
    accounts.current.freshLogin &&
    !accounts.current.passkeySignup &&
    devices.supported &&
    devices.loaded &&
    devices.items.length === 0 &&
    !isCreatePasskeyDismissed();

  if (!eligible) return null;
  // Clearing the fresh-login flag on close means the prompt shows once per fresh
  // login: after it closes, whether the user created a passkey or dismissed it,
  // the flag is false so it does not reappear this session.
  return <CreatePasskeyPrompt onClose={() => setFreshLogin(false)} />;
}

export default function App() {
  // Set by the API layer on any 503 from the host's maintenance gate. Read via
  // useSyncExternalStore because the signal originates in plain functions in
  // lib/api.ts, outside React -- this is exactly the tearing-safe subscription
  // that API exists for.
  const maintenance = useSyncExternalStore(subscribeMaintenance, isMaintenance, isMaintenance);
  useThemeEffect();
  useEnvironment();
  useSessionExpiredRedirect();
  const authed = useAuthed();
  useDocumentLanguage(authed);
  const account = useSlice('accounts').current;
  useDevices(authed, account.sessionEpoch);
  useEffect(() => {
    if (!authed) return;
    return startHomePreferences();
  }, [authed, account.sessionEpoch, account.orgId, account.userId]);

  // data-masthead is set by AppHeader itself, not here. The bar no longer renders
  // on every authenticated screen — the home has none — so "is there a bar" is no
  // longer the same question as "is there a session", and only the bar knows.

  // AFTER every hook above, so the early return never changes hook order.
  //
  // Replaces the entire shell rather than rendering a banner: during a window
  // every /v1 call fails, so a still-mounted app would be a wall of individual
  // error states, each implying something went wrong with that one thing. One
  // screen saying the wallet is paused is the truthful summary.
  if (maintenance) return <Maintenance />;

  return (
    <PageHeaderProvider>
      {authed && <AppHeader />}
      <main id="main" tabIndex={-1} data-region="main">
        {/* The boundary OUTSIDE the Suspense: a lazy chunk that fails after
            lazyWithReload's single reload rejects during render, and without a
            boundary that unmounted the entire app into a blank screen — the
            2026-08-20 incident. Inside it, the same protection covers every
            other render-time crash for free. */}
        {/* Matched-screen keys tear down departing owners during a lazy wait.
            Query/hash/case aliases retain the same screen and local draft. */}
        <AppErrorBoundary>
          <WelcomeGate>
          <Routes>
            <Route
              path={ROUTE_WELCOME}
              element={authed ? <Navigate to="/" replace /> : (
                <RouteScreen key="Welcome"><Welcome /></RouteScreen>
              )}
            />
            {/* Pre-auth onboarding: the welcome choice lands here. An authenticated
                visitor has no account to create, so they go to the app. */}
            <Route
              path={ROUTE_START}
              element={authed ? <Navigate to="/" replace /> : (
                <RouteScreen key="Start"><Start /></RouteScreen>
              )}
            />
            <Route
              path={ROUTE_LOGIN}
              element={authed ? <Navigate to="/" replace /> : (
                <RouteScreen key="Login"><Login /></RouteScreen>
              )}
            />
            {/* Public on purpose. It reads as a child of /settings but is a separate flat
                route: /settings is behind RequireAuth, and the visitor this page exists
                for has no session. Signed-in users reach the same screen. */}
            <Route
              path={ROUTE_LANGUAGE_APPEARANCE}
              element={<RouteScreen key="LanguageAppearance"><LanguageAppearance /></RouteScreen>}
            />
            <Route
              path={ROUTE_OAUTH_CALLBACK}
              element={<RouteScreen key="OAuthCallback"><OAuthCallback /></RouteScreen>}
            />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <RouteScreen key="Dashboard" loading={<HomeSkeleton />}>
                    <Dashboard />
                  </RouteScreen>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTE_PROVISIONING}
              element={
                <RequireAuth>
                  <RouteScreen key="Provisioning"><Provisioning /></RouteScreen>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTE_CREATE}
              element={
                <RequireAuth>
                  <RouteScreen key="Create"><Create /></RouteScreen>
                </RequireAuth>
              }
            />
            <Route
              path="/send"
              element={
                <RequireAuth>
                  <RouteScreen key="Send"><Send /></RouteScreen>
                </RequireAuth>
              }
            />
            <Route
              path="/receive"
              element={
                <RequireAuth>
                  <RouteScreen key="Receive" loading={<ReceiveSkeleton />}>
                    <Receive />
                  </RouteScreen>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTE_SWAP}
              element={
                <RequireAuth>
                  <RouteScreen key="Swap" loading={<SwapSkeleton />}>
                    <Swap />
                  </RouteScreen>
                </RequireAuth>
              }
            />
            <Route
              path="/earn"
              element={
                <RequireAuth>
                  <RouteScreen key="Earn"><Earn /></RouteScreen>
                </RequireAuth>
              }
            />
            <Route
              path="/activity"
              element={
                <RequireAuth>
                  <RouteScreen key="Activity" loading={<ActivitySkeleton />}>
                    <Activity />
                  </RouteScreen>
                </RequireAuth>
              }
            />
            <Route
              path="/approvals"
              element={
                <RequireAuth>
                  <RouteScreen key="Approvals"><Approvals /></RouteScreen>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTE_SECURITY}
              element={
                <RequireAuth>
                  <RouteScreen key="SecuritySettings"><SecuritySettings /></RouteScreen>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTE_DEVELOPER}
              element={
                <RequireAuth>
                  <RouteScreen key="DeveloperSettings"><DeveloperSettings /></RouteScreen>
                </RequireAuth>
              }
            />
            {/* No bare /settings: the menu drawer is the hub now, so the old hub page
                had no rows left to list and was deleted. A bookmark to it falls through
                to the catch-all below and lands on the dashboard, which is where an
                unknown route already goes — no dead end, and no redirect rule to keep. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </WelcomeGate>
        </AppErrorBoundary>
      </main>
      {authed && <AppNav />}
      {authed && <CreatePasskeyPromptGate />}
      {authed && <SwapTrackerGate />}
      <Toaster />
    </PageHeaderProvider>
  );
}
